import { useEffect, useState, type CSSProperties, type KeyboardEvent } from "react";
import { ApiError } from "../../api/client";
import type {
  Authorization,
  DeviceEntry,
  DeviceType,
  PrinterEntry,
  PrintingSettings,
  PrintingState,
  PrintStatus,
} from "../../api/types";
import { useTill } from "../../app/till-context";
import { t } from "../../i18n";
import { ConfirmDialog } from "../../ui/ConfirmDialog";
import { productsIn, sellableCategories } from "../sale/menu-view";
import { CallerIdSection } from "../calls/CallerIdSection";
import { AddPrinterDialog } from "./AddPrinterDialog";
import { DEVICE_TYPES, NewDeviceDialog } from "./NewDeviceDialog";
import { isException, nothingRouted, printersOf, resetProduct, routedCount, setCategory, setProduct } from "./kitchen";
import { OutputTable } from "./OutputTable";

interface Props {
  /** The owner's Beheer code. */
  authorization: Authorization;
}

const COPIES = [1, 2, 3];
/**
 * The printable widths in dots per paper (Furkan, 09/10/2026: a Bixolon printed every ticket too large).
 * Most 80 mm printers print 576 dots, Bixolon 512, a few 640; on 58 mm 384, some 360 or 432.
 */
const DOTS: Record<58 | 80, number[]> = { 80: [640, 576, 512], 58: [432, 384, 360] };

const day = (iso: string) => {
  const date = new Date(iso);
  return `${String(date.getDate()).padStart(2, "0")}/${String(date.getMonth() + 1).padStart(2, "0")}/${String(date.getFullYear())}`;
};

/** A text field that saves when it is left or with Enter (the prototype's onchange). */
function SaveField({ value, label, onSave }: { value: string; label: string; onSave: (text: string) => void }) {
  const [text, setText] = useState<string | null>(null);
  const done = () => {
    const next = text?.trim();
    if (next && next !== value) onSave(next);
    setText(null);
  };
  return (
    <input
      value={text ?? value}
      aria-label={label}
      maxLength={60}
      onChange={(event) => {
        setText(event.target.value);
      }}
      onBlur={done}
      onKeyDown={(event: KeyboardEvent<HTMLInputElement>) => {
        if (event.key === "Enter") event.currentTarget.blur();
      }}
    />
  );
}

/**
 * Beheer › Printers & toestellen, as in the prototype: 1 · Printers, 2 · Wat drukt elke printer af? (with
 * the Z-rapport and Per kassa), 3 · Keukenbonnen per product, 4 · Toestellen and 5 · Betaalterminal,
 * plus 6 · Nummerherkenning (the caller-ID device and the network address for a VoIP exchange).
 * Every change is saved at once; the Tafo app for Windows prints (block I).
 */
export function PrintersScreen({ authorization }: Props) {
  const { api, menu, status, report, toast, printingRevision, deviceChanged, printStatus } = useTill();
  const [state, setState] = useState<PrintingState | null>(null);
  const [adding, setAdding] = useState(false);
  const [removing, setRemoving] = useState<PrinterEntry | null>(null);
  const [unpairing, setUnpairing] = useState<DeviceEntry | null>(null);
  const [newDevice, setNewDevice] = useState(false);
  const [open, setOpen] = useState<ReadonlySet<string>>(new Set());
  const text = t.printing;
  const staffToken = authorization.token;

  useEffect(() => {
    let current = true;
    api
      .request<PrintingState>("GET", "/device/printing", { staffToken, retry: true })
      .then((answer) => {
        if (current) setState(answer);
      })
      .catch(report);
    return () => {
      current = false;
    };
  }, [api, staffToken, report, printingRevision]);

  if (state === null || menu.menu === null) return <div className="panel admin-section" aria-busy="true" />;
  const data = menu.menu;
  const { printers, settings, devices } = state;

  const saveSettings = async (next: PrintingSettings, message?: string) => {
    setState({ ...state, settings: next });
    try {
      const answer = await api.request<{ settings: PrintingSettings }>("PUT", "/device/printing", {
        staffToken,
        body: next,
      });
      setState((current) => current && { ...current, settings: answer.settings });
      if (message) toast(message);
    } catch (error) {
      report(error);
    }
  };

  const savePrinter = async (printer: PrinterEntry, change: Partial<PrinterEntry>, message?: string) => {
    const next = { ...printer, ...change };
    try {
      const answer = await api.request<{ printer: PrinterEntry }>("PUT", `/device/printers/${printer.id}`, {
        staffToken,
        body: {
          name: next.name,
          paper: next.paper,
          drawer: next.drawer,
          kitchenCopies: next.kitchenCopies,
          // another paper: the width the app chooses for it, until one is set again
          ...(change.dots !== undefined
            ? { dots: change.dots }
            : change.paper !== undefined && change.paper !== printer.paper
              ? { dots: null }
              : {}),
        },
      });
      setState(
        (current) =>
          current && {
            ...current,
            printers: current.printers.map((item) => (item.id === printer.id ? answer.printer : item)),
          },
      );
      if (message) toast(message);
    } catch (error) {
      report(error);
    }
  };

  const testPrinter = async (printer: PrinterEntry) => {
    try {
      await api.request("POST", `/device/printers/${printer.id}/test`, { staffToken });
      toast(text.testSent(printer.name, printer.paper));
    } catch (error) {
      report(error);
    }
  };

  const removePrinter = async (printer: PrinterEntry) => {
    setRemoving(null);
    try {
      await api.request("DELETE", `/device/printers/${printer.id}`, { staffToken });
      const answer = await api.request<PrintingState>("GET", "/device/printing", { staffToken });
      setState(answer);
      toast(text.removed(printer.name));
    } catch (error) {
      report(error);
    }
  };

  const saveDevice = async (device: DeviceEntry, change: Partial<DeviceEntry>) => {
    const next = { ...device, ...change };
    try {
      const answer = await api.request<{ device: DeviceEntry }>("PUT", `/device/devices/${device.id}`, {
        staffToken,
        body: { name: next.name, type: next.type },
      });
      setState(
        (current) =>
          current && {
            ...current,
            devices: current.devices.map((item) => (item.id === device.id ? answer.device : item)),
          },
      );
      if (device.id === status.device.id) {
        deviceChanged({ ...status.device, name: answer.device.name, type: answer.device.type });
      }
    } catch (error) {
      report(error);
    }
  };

  const unpair = async (device: DeviceEntry) => {
    setUnpairing(null);
    try {
      await api.request("DELETE", `/device/devices/${device.id}`, { staffToken });
      toast(text.unpaired(device.name));
      // this device itself: back to pairing, as the prototype opens "Nieuw toestel"
      if (device.id === status.device.id) {
        report(new ApiError(401, "device_not_paired", ""));
        return;
      }
      setState(
        (current) => current && { ...current, devices: current.devices.filter((item) => item.id !== device.id) },
      );
    } catch (error) {
      report(error);
    }
  };

  const categories = sellableCategories(data);
  /** The till computers: each can have its own receipt printer ("Per kassa") and a payment terminal. */
  const tills = devices.filter((device) => device.type === "pc");

  return (
    <div className="panel admin-section printing">
      <h2>{text.title}</h2>

      <div className="zone">{text.zones.printers}</div>
      {printers.length === 0 ? (
        <p className="note">{text.noPrinters}</p>
      ) : (
        <div className="tablewrap">
          <table className="ptbl stack">
            <thead>
              <tr>
                <th>{text.columns.name}</th>
                <th>{text.columns.printer}</th>
                <th>{text.columns.paper}</th>
                <th>{text.columns.drawer}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {printers.map((printer) => (
                <tr key={printer.id}>
                  <td className="full">
                    <SaveField
                      value={printer.name}
                      label={text.nameLabel}
                      onSave={(name) => void savePrinter(printer, { name })}
                    />
                  </td>
                  <td className="full">
                    <b>{printer.model}</b>
                    <br />
                    <span className="note">
                      {printer.connection}
                      {printer.viaName && text.via(printer.viaName)}
                    </span>
                  </td>
                  <td data-label={text.columns.paper}>
                    <div className="seg" role="group" aria-label={`${text.columns.paper} ${printer.name}`}>
                      {([80, 58] as const).map((paper) => (
                        <button
                          key={paper}
                          type="button"
                          aria-pressed={printer.paper === paper}
                          onClick={() => void savePrinter(printer, { paper })}
                        >
                          {paper}
                        </button>
                      ))}
                    </div>
                    {printer.dots !== undefined && (
                      <label className="pdots" title={text.dots.hint}>
                        <span className="note">{text.dots.label}</span>
                        <select
                          aria-label={text.dots.of(printer.name)}
                          value={printer.dots ?? ""}
                          onChange={(event) => {
                            const dots = event.target.value === "" ? null : Number(event.target.value);
                            void savePrinter(printer, { dots }, text.dots.saved(printer.name, dots));
                          }}
                        >
                          <option value="">{text.dots.auto}</option>
                          {DOTS[printer.paper].map((dots) => (
                            <option key={dots} value={dots}>
                              {text.dots.option(dots)}
                            </option>
                          ))}
                        </select>
                      </label>
                    )}
                  </td>
                  <td data-label={text.columns.drawer}>
                    <div className="seg" role="group" aria-label={`${text.columns.drawer} ${printer.name}`}>
                      <button
                        type="button"
                        aria-pressed={printer.drawer}
                        onClick={() => void savePrinter(printer, { drawer: true })}
                      >
                        {text.yes}
                      </button>
                      <button
                        type="button"
                        aria-pressed={!printer.drawer}
                        onClick={() => void savePrinter(printer, { drawer: false })}
                      >
                        {text.no}
                      </button>
                    </div>
                  </td>
                  <td className="end">
                    <div className="rowbtns">
                      <button
                        type="button"
                        className="btn small"
                        aria-label={`${text.test} ${printer.name}`}
                        onClick={() => void testPrinter(printer)}
                      >
                        {text.test}
                      </button>
                      <button
                        type="button"
                        className="btn small danger"
                        aria-label={`${text.remove} ${printer.name}`}
                        disabled={printers.length < 2}
                        onClick={() => {
                          setRemoving(printer);
                        }}
                      >
                        {text.remove}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <button
        type="button"
        className="btn addprinter"
        onClick={() => {
          setAdding(true);
        }}
      >
        {text.add}
      </button>

      <div className="zone">{text.zones.output}</div>
      {printers.length > 0 && (
        <OutputTable
          printers={printers}
          settings={settings}
          onSave={(next, message) => void saveSettings(next, message)}
        />
      )}
      <div className="zrow">
        <span className="note">{text.zNote}</span>
        <select
          aria-label={text.zPrinter}
          value={settings.z.printerId ?? ""}
          disabled={printers.length === 0}
          onChange={(event) => void saveSettings({ ...settings, z: { ...settings.z, printerId: event.target.value } })}
        >
          {printers.length === 0 && <option value="">—</option>}
          {printers.map((printer) => (
            <option key={printer.id} value={printer.id}>
              {printer.name} · {printer.paper} mm
            </option>
          ))}
        </select>
        <select
          aria-label={text.zCount}
          value={settings.z.copies}
          onChange={(event) =>
            void saveSettings({ ...settings, z: { ...settings.z, copies: Number(event.target.value) } })
          }
        >
          {COPIES.map((count) => (
            <option key={count} value={count}>
              {count}×
            </option>
          ))}
        </select>
      </div>
      {tills.length > 1 && printers.length > 1 && (
        <div className="pertill">
          <b>{text.perTill}</b>
          <p className="note">{text.perTillNote}</p>
          <div className="tablewrap">
            <table className="stack">
              <tbody>
                {tills.map((till) => {
                  const own = settings.byDevice[till.id] ?? "";
                  return (
                    <tr key={till.id}>
                      <td className="full">
                        {till.name}
                        {till.id === status.device.id && <span className="pill ok thisdev">{text.thisDevice}</span>}
                      </td>
                      <td className="grow">
                        <select
                          aria-label={text.perTillOf(till.name)}
                          value={own}
                          onChange={(event) => {
                            const chosen = printers.find((printer) => printer.id === event.target.value) ?? null;
                            const others = Object.entries(settings.byDevice).filter(([id]) => id !== till.id);
                            const byDevice = Object.fromEntries(chosen ? [...others, [till.id, chosen.id]] : others);
                            void saveSettings(
                              { ...settings, byDevice },
                              text.perTillSaved(till.name, chosen?.name ?? null),
                            );
                          }}
                        >
                          <option value="">{text.perTillDefault}</option>
                          {printers.map((printer) => (
                            <option key={printer.id} value={printer.id}>
                              {printer.name} · {printer.paper} mm
                            </option>
                          ))}
                        </select>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="zone">{text.zones.kitchen}</div>
      {printers.length > 0 && nothingRouted(settings, printers) && (
        <p className="pill warn kwarn" role="status">
          {text.nothingRouted}
        </p>
      )}
      {printers.length > 0 && (
        <div className="tablewrap">
          {/* on a phone every row is the name over the whole width with the toggles under the printers'
              names, so nothing slides however many printers there are (Furkan, 10/10/2026) */}
          <table className="ktbl" style={{ "--printers": printers.length } as CSSProperties}>
            <thead>
              <tr>
                <th>{text.columns.product}</th>
                {printers.map((printer) => (
                  <th key={printer.id} className="c">
                    {printer.name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {categories.map((category) => {
                const products = productsIn(data, category.id);
                const exceptions = products.filter((product) => isException(settings.kitchen, product));
                const expanded = open.has(category.id);
                return [
                  <tr key={category.id} className="kcat">
                    <td>
                      <button
                        type="button"
                        className="linkbtn"
                        aria-expanded={expanded}
                        onClick={() => {
                          const next = new Set(open);
                          if (expanded) next.delete(category.id);
                          else next.add(category.id);
                          setOpen(next);
                        }}
                      >
                        {expanded ? "▾" : "▸"} {category.name} <span className="note">({products.length})</span>
                      </button>
                      {exceptions.length > 0 && (
                        <div className="exnote">
                          {text.exceptions(exceptions.map((product) => product.name).join(", "))}
                        </div>
                      )}
                    </td>
                    {printers.map((printer) => {
                      const count = routedCount(data, settings.kitchen, category.id, printer.id);
                      const all = products.length > 0 && count === products.length;
                      return (
                        <td key={printer.id} className="c">
                          <button
                            type="button"
                            className="btn tog sm"
                            aria-pressed={all}
                            aria-label={text.on(category.name, printer.name)}
                            onClick={() =>
                              void saveSettings({
                                ...settings,
                                kitchen: setCategory(data, settings.kitchen, category.id, printer.id, !all),
                              })
                            }
                          >
                            {all ? "✓" : "–"}
                          </button>
                          {count > 0 && count < products.length && (
                            <span className="note">
                              {" "}
                              {count}/{products.length}
                            </span>
                          )}
                        </td>
                      );
                    })}
                  </tr>,
                  ...(expanded
                    ? products.map((product) => {
                        const exception = isException(settings.kitchen, product);
                        const routed = printersOf(settings.kitchen, product);
                        return (
                          <tr key={product.id} className={`kprod${exception ? " exc" : ""}`}>
                            <td>
                              {product.name}
                              {exception && (
                                <>
                                  {" "}
                                  <span className="pill warn">{text.exception}</span>{" "}
                                  <button
                                    type="button"
                                    className="linkbtn note"
                                    onClick={() =>
                                      void saveSettings({
                                        ...settings,
                                        kitchen: resetProduct(settings.kitchen, product),
                                      })
                                    }
                                  >
                                    {text.likeCategory}
                                  </button>
                                </>
                              )}
                            </td>
                            {printers.map((printer) => {
                              const on = routed.includes(printer.id);
                              return (
                                <td key={printer.id} className="c">
                                  <button
                                    type="button"
                                    className="btn tog sm"
                                    aria-pressed={on}
                                    aria-label={text.on(product.name, printer.name)}
                                    onClick={() =>
                                      void saveSettings({
                                        ...settings,
                                        kitchen: setProduct(data, settings.kitchen, product, printer.id, !on),
                                      })
                                    }
                                  >
                                    {on ? "✓" : "–"}
                                  </button>
                                </td>
                              );
                            })}
                          </tr>
                        );
                      })
                    : []),
                ];
              })}
              <tr>
                <td className="note">{text.kitchenCopies}</td>
                {printers.map((printer) => (
                  <td key={printer.id} className="c">
                    <select
                      aria-label={text.kitchenCopiesOf(printer.name)}
                      value={printer.kitchenCopies}
                      onChange={(event) => void savePrinter(printer, { kitchenCopies: Number(event.target.value) })}
                    >
                      {COPIES.map((count) => (
                        <option key={count} value={count}>
                          {count}×
                        </option>
                      ))}
                    </select>
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      )}

      <div className="zone">{text.zones.devices}</div>
      <div className="tablewrap">
        <table className="ptbl stack">
          <thead>
            <tr>
              <th>{text.columns.name}</th>
              <th>{text.columns.kind}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {devices.map((device) => (
              <tr key={device.id}>
                <td className="full">
                  <SaveField
                    value={device.name}
                    label={text.deviceName}
                    onSave={(name) => void saveDevice(device, { name })}
                  />
                  {device.id === status.device.id && <span className="pill ok thisdev">{text.thisDevice}</span>}
                </td>
                <td className="full">
                  <select
                    aria-label={`${text.kindLabel} ${device.name}`}
                    value={device.type}
                    onChange={(event) => void saveDevice(device, { type: event.target.value as DeviceType })}
                  >
                    {DEVICE_TYPES.map((type) => (
                      <option key={type} value={type}>
                        {text.deviceTypes[type]}
                      </option>
                    ))}
                  </select>
                  <br />
                  <span className="note">{text.pairedOn(day(device.createdAt), text.deviceRoles[device.role])}</span>
                  {device.type === "pc" && device.role === "till" && printStatus !== null && (
                    // blok I: the Tafo app for Windows on this computer prints (its USB printers too)
                    <AgentLine agent={printStatus.agents.find((agent) => agent.deviceId === device.id)} />
                  )}
                </td>
                <td className="end">
                  <button
                    type="button"
                    className="btn small danger"
                    aria-label={`${text.unpair} ${device.name}`}
                    onClick={() => {
                      setUnpairing(device);
                    }}
                  >
                    {text.unpair}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <button
        type="button"
        className="btn addprinter"
        onClick={() => {
          setNewDevice(true);
        }}
      >
        {text.addDevice}
      </button>

      <div className="zone">
        {text.zones.terminal} <span className="note">{text.zones.optional}</span>
      </div>
      <div className="tablewrap">
        <table className="ptbl stack">
          <thead>
            <tr>
              <th>{text.columns.till}</th>
              <th>{text.columns.terminal}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {tills.length === 0 ? (
              <tr>
                <td colSpan={3} className="note">
                  {text.noTill}
                </td>
              </tr>
            ) : (
              tills.map((device) => (
                <tr key={device.id}>
                  <td>
                    <b>{device.name}</b>
                  </td>
                  <td>
                    <span className="note">{text.looseTerminal}</span>
                  </td>
                  <td className="end">
                    <button type="button" className="btn small" disabled title={text.linkLater}>
                      {text.linkTerminal}
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <CallerIdSection staffToken={authorization.token} />

      {adding && (
        <AddPrinterDialog
          authorization={authorization}
          state={state}
          onAdded={() => {
            setAdding(false);
            void api.request<PrintingState>("GET", "/device/printing", { staffToken }).then(setState).catch(report);
          }}
          onClose={() => {
            setAdding(false);
          }}
        />
      )}
      {removing && (
        <ConfirmDialog
          message={text.removeConfirm(removing.name)}
          confirm={text.removeButton}
          onConfirm={() => void removePrinter(removing)}
          onClose={() => {
            setRemoving(null);
          }}
        />
      )}
      {newDevice && (
        <NewDeviceDialog
          authorization={authorization}
          devices={devices}
          onClose={() => {
            setNewDevice(false);
          }}
        />
      )}
      {unpairing && (
        <ConfirmDialog
          message={text.unpairConfirm(unpairing.name)}
          confirm={text.unpair}
          onConfirm={() => void unpair(unpairing)}
          onClose={() => {
            setUnpairing(null);
          }}
        />
      )}
    </div>
  );
}

/** "🖨 Tafo-app actief · versie 1.0.0" or, in red, "🖨 Tafo-app niet actief op deze computer". */
function AgentLine({ agent }: { agent: PrintStatus["agents"][number] | undefined }) {
  return agent ? (
    <span className="note agentline">{t.print.agentOn(agent.version)}</span>
  ) : (
    <span className="note agentline off">{t.print.agentOff}</span>
  );
}
